import { Pipe, PipeTransform } from '@angular/core';

/**
 * Custom pipe to handle European date format "DD.MM.YYYY HH:mm"
 * Parses European format and outputs formatted date string
 *
 * Usage: {{ dateString | europeanDate : 'dd.MM.yyyy HH:mm' }}
 */
@Pipe({
  name: 'europeanDate',
  pure: true
})
export class EuropeanDatePipe implements PipeTransform {

  transform(value: string | Date | null | undefined, format: string = 'dd.MM.yyyy HH:mm'): string | null {
    if (!value) {
      return null;
    }

    let date: Date | null = null;

    // If already a Date object, use it directly
    if (value instanceof Date) {
      date = value;
    } else {
      // Try to parse the European date format: "DD.MM.YYYY HH:mm" or "DD.MM.YYYY"
      date = this.parseEuropeanDate(value);

      // Fallback: try standard Date parsing (ISO format, etc.)
      if (!date) {
        try {
          const standardDate = new Date(value);
          if (!isNaN(standardDate.getTime())) {
            date = standardDate;
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }
    }

    // If we have a valid date, format it
    if (date) {
      return this.formatDate(date, format);
    }

    // If all parsing fails, return the original value
    return typeof value === 'string' ? value : null;
  }

  /**
   * Parse European date format: "DD.MM.YYYY HH:mm" or "DD.MM.YYYY"
   */
  private parseEuropeanDate(dateString: string): Date | null {
    if (!dateString || typeof dateString !== 'string') {
      return null;
    }

    // Pattern: DD.MM.YYYY HH:mm or DD.MM.YYYY
    const regex = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;
    const match = dateString.trim().match(regex);

    if (!match) {
      return null;
    }

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1; // JavaScript months are 0-indexed
    const year = parseInt(match[3], 10);
    const hours = match[4] ? parseInt(match[4], 10) : 0;
    const minutes = match[5] ? parseInt(match[5], 10) : 0;
    const seconds = match[6] ? parseInt(match[6], 10) : 0;

    // Validate date components
    if (month < 0 || month > 11 || day < 1 || day > 31) {
      return null;
    }

    const date = new Date(year, month, day, hours, minutes, seconds);

    // Verify the date is valid (handles cases like Feb 30)
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
      return null;
    }

    return date;
  }

  /**
   * Format a Date object according to the specified format
   * Supports: dd, MM, yyyy, HH, mm, ss
   */
  private formatDate(date: Date, format: string): string {
    const pad = (num: number, size: number = 2): string => {
      return num.toString().padStart(size, '0');
    };

    const replacements: { [key: string]: string } = {
      'dd': pad(date.getDate()),
      'MM': pad(date.getMonth() + 1),
      'yyyy': date.getFullYear().toString(),
      'yy': date.getFullYear().toString().slice(-2),
      'HH': pad(date.getHours()),
      'mm': pad(date.getMinutes()),
      'ss': pad(date.getSeconds())
    };

    let result = format;

    // Replace in order of length (longest first) to avoid partial replacements
    const keys = Object.keys(replacements).sort((a, b) => b.length - a.length);
    for (const key of keys) {
      result = result.replace(new RegExp(key, 'g'), replacements[key]);
    }

    return result;
  }
}
